import { useState, useEffect, useMemo } from 'react';
import { gql, useLazyQuery, useQuery, useMutation } from '@apollo/client';
import { Box, Typography, Button, TextField, Tooltip, Pagination } from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import { useSelector } from 'react-redux';

import { FlexEnd } from '../../../commonStyles';
import { ICommentArray, IComment } from '../../../types/comment';
import { selectProject } from '../../../features/project/projectSlice';
import StandardTable from '../../tables/StandardTable';
import { useAppDispatch } from '../../../app/hooks';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../features/snackbar/snackbarSlice';
import { selectUser } from '../../../features/auth/authSlice';
import { convertAllDates } from '../../../utils/Functions';

const Notices = () => {
  const user = useSelector(selectUser);
  const project = useSelector(selectProject);
  const storeDispatch = useAppDispatch();
  const [pinnedComments, setPinnedComments] = useState<IComment[]>([]);
  const [comments, setComments] = useState<IComment[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const [selectedNotice, setSelectedNotice] = useState<string>('');
  const [notice, setNotice] = useState<string>('');
  const [count, setCount] = useState<number>(0);

  useQuery<ICommentArray>(GETPINNEDCOMMENTS, {
    variables: { filter: { pinned: true, type: 'notice', project: project._id }, sort: 'PINNED' },
    onCompleted: (data) => {
      setPinnedComments(data.commentMany);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [getComments] = useLazyQuery(GETCOMMENTS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      setCount(data.commentPagination.count);
      setComments(data.commentPagination.items);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [createComment] = useMutation(CREATECOMMENT, {
    onCompleted: (data) => {
      setPinnedComments([data.commentCreateOne.record, ...pinnedComments]);
      setOpen(false);
      setSelectedNotice('');
      setNotice('');
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updateComment] = useMutation(UPDATECOMMENT, {
    onCompleted: (data) => {
      if (data.commentUpdateById.record.pinned) {
        let comment = comments.find((comment: IComment) => comment._id === data.commentUpdateById.record._id);
        if (comment) {
          let comment = comments.filter((comment: IComment) => comment._id !== data.commentUpdateById.record._id);
          setPinnedComments([data.commentUpdateById.record, ...pinnedComments]);
          setComments(comment);
        } else {
          let allComments = pinnedComments.map((comment: IComment) => {
            if (comment._id === data.commentUpdateById.record._id) {
              return data.commentUpdateById.record;
            } else return comment;
          });
          setPinnedComments(allComments);
        }
        storeDispatch(showSuccessSnackbar('Notice Updated!'));
      } else {
        let comment = pinnedComments.find((comment: IComment) => comment._id === data.commentUpdateById.record._id);
        if (comment) {
          let comment = pinnedComments.filter((comment: IComment) => comment._id !== data.commentUpdateById.record._id);
          setComments([data.commentUpdateById.record, ...comments]);
          setPinnedComments(comment);
        } else {
          let allComments = pinnedComments.map((comment: IComment) => {
            if (comment._id === data.commentUpdateById.record._id) {
              return data.commentUpdateById.record;
            } else return comment;
          });
          setComments(allComments);
        }
        storeDispatch(showSuccessSnackbar('Notice Updated!'));
      }
      setOpen(false);
      setSelectedNotice('');
      setNotice('');
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  useEffect(() => {
    getComments({
      variables: {
        filter: {
          project: project._id,
          type: 'notice',
          pinned: false,
        },
        perPage: 10,
        page: pageNumber,
      },
    });
  }, [pageNumber]);

  const columns = useMemo(() => {
    let columns = [
      {
        Header: 'Notice',
        accessor: (rowData: any) => {
          return (
            <Box
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                if (rowData.pinned) {
                  setOpen(true);
                  setSelectedNotice(rowData._id);
                  setNotice(rowData.comment);
                }
              }}
            >
              <strong>{rowData.comment}</strong>
            </Box>
          );
        },
      },
      {
        Header: 'Created Date',
        accessor: (rowData: any) => convertAllDates(rowData.createdAt, 'PPpp'),
      },
      {
        Header: 'Updated Date',
        accessor: (rowData: any) => convertAllDates(rowData.updatedAt, 'PPpp'),
      },
      {
        Header: 'Last User Updated',
        accessor: (rowData: any) => rowData.user.fullName,
      },
      {
        Header: ' ',
        accessor: (rowData: any) => {
          if (rowData.pinned) {
            return (
              <Tooltip title="Unpin Notice">
                <PushPinIcon
                  onClick={() =>
                    updateComment({
                      variables: {
                        _id: rowData._id,
                        record: {
                          user: user?._id,
                          project: project._id,
                          type: 'notice',
                          comment: rowData.comment,
                          pinned: rowData.pinned ? false : true,
                          step: null,
                          deleted: false,
                        },
                      },
                    })
                  }
                  sx={{ cursor: 'pointer', ml: 1, transform: 'rotate(180deg)', '-webkit-transform': 'rotate(180deg)', color: 'red' }}
                />
              </Tooltip>
            );
          } else {
            return (
              <Tooltip title="Pin Notice">
                <PushPinIcon
                  onClick={() =>
                    updateComment({
                      variables: {
                        _id: rowData._id,
                        record: {
                          user: user?._id,
                          project: project._id,
                          type: 'notice',
                          comment: rowData.comment,
                          pinned: rowData.pinned ? false : true,
                          step: null,
                          deleted: false,
                        },
                      },
                    })
                  }
                  sx={{ cursor: 'pointer', ml: 1 }}
                />
              </Tooltip>
            );
          }
        },
      },
    ];

    return columns;
  }, []);

  const handleNotice = () => {
    if (selectedNotice) {
      updateComment({
        variables: {
          _id: selectedNotice,
          record: {
            user: user?._id,
            project: project._id,
            type: 'notice',
            comment: notice,
            pinned: true,
            step: null,
            deleted: false,
          },
        },
      });
    } else {
      if (!notice) return storeDispatch(showErrorSnackbar('No Notice Found'));
      createComment({
        variables: {
          record: {
            user: user?._id,
            project: project._id,
            type: 'notice',
            comment: notice,
            pinned: true,
            step: null,
            deleted: false,
          },
        },
      });
    }
  };

  return (
    <Box>
      {open ? (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            name={'addNotice'}
            onChange={(e: any) => setNotice(e.target.value)}
            value={notice}
            label={'Add Notice'}
            placeholder={'Enter Text'}
          />
        </Box>
      ) : null}
      <FlexEnd sx={{ mb: 2 }}>
        {open ? (
          <Button
            color="success"
            variant="contained"
            sx={{ mr: 1 }}
            onClick={() => {
              handleNotice();
            }}
          >
            {selectedNotice ? 'Update' : 'Create'}
          </Button>
        ) : null}
        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            setSelectedNotice('');
            setNotice('');
            setOpen(!open);
          }}
        >
          {open ? 'Cancel' : 'Add Notice'}
        </Button>
      </FlexEnd>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ mb: 2 }} variant={'h2'}>
          <strong>Current Notices</strong>
        </Typography>
        <StandardTable columns={columns} data={pinnedComments} />
      </Box>
      <Box>
        <Typography sx={{ mb: 2 }} variant={'h2'}>
          <strong>Past Notices</strong>
        </Typography>
        <StandardTable columns={columns} data={comments} />
        <Box sx={{ mt: 2 }}>
          <Pagination
            sx={{ '& .MuiPagination-ul': { justifyContent: 'center' } }}
            count={Math.ceil(count / 10)}
            page={pageNumber}
            siblingCount={window.innerWidth < 600 ? 0 : 3}
            showFirstButton
            showLastButton
            onChange={(e: any, value: number) => setPageNumber(value)}
          />
        </Box>
      </Box>
    </Box>
  );
};

const CREATECOMMENT = gql`
  mutation commentCreateOne($record: CreateOneCommentInput!) {
    commentCreateOne(record: $record) {
      record {
        _id
        comment
        pinned
        user {
          fullName
        }
        type
        createdAt
        updatedAt
      }
    }
  }
`;

const UPDATECOMMENT = gql`
  mutation commentUpdateById($_id: MongoID!, $record: UpdateByIdCommentInput!) {
    commentUpdateById(_id: $_id, record: $record) {
      record {
        _id
        comment
        pinned
        user {
          fullName
        }
        type
        createdAt
        updatedAt
      }
    }
  }
`;

const GETPINNEDCOMMENTS = gql`
  query commentMany($filter: FilterFindManyCommentInput, $sort: SortFindManyCommentInput) {
    commentMany(filter: $filter, sort: $sort) {
      _id
      comment
      pinned
      user {
        fullName
      }
      type
      createdAt
      updatedAt
    }
  }
`;

const GETCOMMENTS = gql`
  query commentPagination($filter: FilterFindManyCommentInput, $page: Int!, $perPage: Int!, $sort: SortFindManyCommentInput) {
    commentPagination(filter: $filter, page: $page, perPage: $perPage, sort: $sort) {
      items {
        _id
        comment
        pinned
        user {
          fullName
        }
        type
        createdAt
        updatedAt
      }
      count
    }
  }
`;

export default Notices;
